import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Button,
  Stack,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScoreScale from './ScoreScale';
import { dailyCheckinQuestions } from '../../constants/checkinQuestions';

const DailyCheckinDialog = ({ open, onClose, values, onChange, onComplete, onAfterComplete, submitting = false, isCompleted = false }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentStep, setCurrentStep] = useState(0);

  const questions = dailyCheckinQuestions;
  const handleScaleChange = (questionId, value) => {
    onChange({
      ...values,
      [questionId]: value,
    });
    
    // Auto-advance to next question
    const currentIndex = questions.findIndex(q => q.id === questionId);
    
    if (currentIndex !== -1 && currentIndex < questions.length - 1) {
      setTimeout(() => setCurrentStep(currentIndex + 1), 300);
    }
  };

  const handleComplete = async () => {
    try {
      if (onComplete) {
        await onComplete();
      }
      onAfterComplete?.();
      onClose();
    } catch (e) {
      // onComplete should toast / handle errors; keep dialog open
    }
  };

  const handleDialogClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const isComplete = questions.every(q => values[q.id]);

  // Render question options using ScoreScale for all questions
  const renderQuestionOptions = (question, index) => {
    return (
      <ScoreScale
        value={values[question.id] || ''}
        onChange={(value) => handleScaleChange(question.id, value)}
        icons={question.icons}
        labels={question.labels}
        count={question.labels.length}
        tooltips={question.tooltips || []}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="daily-checkin-title"
      aria-describedby="daily-checkin-description"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: fullScreen ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle id="daily-checkin-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="span" fontWeight="bold">
            Morning Focus Check-in
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} id="daily-checkin-description">
            <Box>
              <Typography variant="h6" fontWeight="medium" gutterBottom>
                Good Morning! 3 Minutes to Set Your Focus and Intention.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The following questions are designed to help you quickly anchor your focus, clarify your top priorities, 
                and establish a successful mindset for the day ahead.
              </Typography>
            </Box>

            <Divider />

            {questions.map((question, index) => (
              <Box key={question.id}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {index + 1}. {question.prompt}
                </Typography>
                {renderQuestionOptions(question, index)}
                
                {/* Show previous answers summary */}
                {values[question.id] && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    ✓ Answered: {question.labels[parseInt(values[question.id])]}
                  </Typography>
                )}
              </Box>
            ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDialogClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={handleComplete} 
          variant="contained" 
          color="primary"
          disabled={!isComplete || submitting}
        >
          {submitting ? 'Saving…' : (isCompleted ? 'Re-submit' : 'Complete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyCheckinDialog;
